import { useState } from 'react'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { getFeaturedPromoCardProperties } from '@/features/shared/analytics/promoCard'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type HeroCardData, type HeroCardCarouselData } from '@/features/cms/components/CMS/types'
import { BotOnlyHeroCard } from '@/features/cms/components/CMS/components/Shelves/BotOnlyHeroCard'
import { HeroCardV2 } from '@/features/cms/components/CMS/components/Shelves/HeroCardV2'
import { getPromotionFromData } from '@/features/cms/components/CMS/utils/promotion'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { CarouselSection } from '@shipt/design-system-carousel'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import styled from 'styled-components'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'

type Props = HeroCardCarouselData & {
  index: number
}

/**
 * This component corresponds to the `hero_card_carousel` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/hero_card_carousel
 * - **Production** - https://cms.shipt.com/content-type/pool/hero_card_carousel
 */
export const HeroCardCarouselV2 = ({ id, data, index, content_type_id }: Props) => {
  const cmsWrapperProps = getCMSWrapperProps({ content_type_id, id })
  const { trackCMSPromoCardViewed, trackCMSElementViewed } = useCMSTrackingEvents()
  const [isCarouselVisible, setIsCarouselVisible] = useState(false)
  const { data: shoppingStores } = useQueryShoppingStores()

  const handleShelfInView = () => {
    trackCMSElementViewed({
      shelf_index: index,
      shelf_name: content_type_id,
    })
    setIsCarouselVisible(true)
  }

  const handleInView = (itemIndex: number, heroCardData: HeroCardData) => {
    const promotion = getPromotionFromData(heroCardData)
    trackCMSPromoCardViewed(
      getFeaturedPromoCardProperties({
        promotion,
        displayFormat: DISPLAY_FORMAT.HORIZONTAL,
        shelfIndex: index,
        listIndex: itemIndex,
        subLocation: cmsWrapperProps.id,
        nextLocation: heroCardData.data.link.url,
      }),
    )
  }

  const limitedCardQty = data.hero_cards.length < 4

  return (
    <HeroCardCarouselContainer {...cmsWrapperProps}>
      <ScrollIntoView
        onInView={handleShelfInView}
        className="fullWidth contentAtTablet"
        threshold={1.0}
      >
        <CarouselSection hasInlineControls>
          <CarouselSection.Carousel
            key={String(isCarouselVisible)}
            hasPagination
            canAutoplay={isCarouselVisible}
            layoutConfig={{
              // @ts-expect-error valid option but types aren't coming thru correctly
              0: {
                slidesPerView: 1.2,
                slidesPerGroup: 1,
              },
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
              },
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 1,
              },
              1280: {
                slidesPerView: limitedCardQty ? 3 : 3.2,
                slidesPerGroup: 1,
              },
            }}
          >
            {data.hero_cards.map((heroCardData, itemIndex) => {
              return (
                <ScrollIntoView
                  onInView={() => handleInView(itemIndex, heroCardData)}
                  // Without a promo id, we can't guarantee that heading will be unique
                  // or that any other properties will be present to check against
                  // oxlint-disable-next-line react/no-array-index-key
                  key={`${heroCardData.data.heading}-${itemIndex}`}
                >
                  <HeroCardV2
                    heroCardData={heroCardData}
                    index={itemIndex}
                    shelfIndex={index}
                    shelfName={cmsWrapperProps.id}
                  />
                  <BotOnlyHeroCard
                    heroCardData={heroCardData}
                    deliveryStores={shoppingStores?.delivery_stores}
                  />
                </ScrollIntoView>
              )
            })}
          </CarouselSection.Carousel>
        </CarouselSection>
      </ScrollIntoView>
    </HeroCardCarouselContainer>
  )
}

const HeroCardCarouselContainer = styled(GridLayoutContainer)`
  width: 100%;

  .swiper {
    padding-top: 0.5rem;
  }

  .swiper-pagination-lock {
    display: none;
  }
`
